import * as React from "react";
import { styled, alpha, keyframes } from "@mui/material/styles";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import MenuIcon from "@mui/icons-material/Menu";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ContactUsDialog from "./ContactUsDialog";
import { Link } from "react-router-dom";
import logo1 from "../assets/logo2.jpg";

// Keyframe animation for fade in
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: "blur(24px)",
  border: "1px solid",
  borderColor: theme.palette.divider,
  backgroundColor: alpha("#ffffff", 0.9),
  boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
  padding: "8px 12px",
  animation: `${fadeIn} 0.5s ease-out`,
}));

const NavButton = styled(Button)(({ theme }) => ({
  fontWeight: 500,
  margin: theme.spacing(0, 1),
  textTransform: "none",
  fontSize: "17px",
  color: "#115097",
  transition: "color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    color: "#4BC8F2",
    transform: "scale(1.05)",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
  },
}));

const LogoImg = styled("img")(({ theme }) => ({
  width: "185px",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const DrawerMenuItem = styled(MenuItem)(({ theme }) => ({
  fontWeight: 500,
  color: "#115097",
  transition: "background-color 0.3s ease, transform 0.3s ease",
  "&:hover": {
    backgroundColor: alpha("#4BC8F2", 0.1),
    transform: "scale(1.02)",
  },
}));

export default function AppAppBar() {
  const [open, setOpen] = React.useState(false);
  const [contactDialogOpen, setContactDialogOpen] = React.useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const handleContactDialogOpen = () => {
    setContactDialogOpen(true);
  };

  const handleContactDialogClose = () => {
    setContactDialogOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        enableColorOnDark
        sx={{
          boxShadow: 0,
          bgcolor: "#ffffff",
          background: "transparent",
          backgroundImage: "none",
          mt: "calc(var(--template-frame-height, 0px) + 28px)",
        }}
      >
        <Container maxWidth="lg">
          <StyledToolbar
            variant="dense"
            disableGutters
            sx={{ background: "white" }}
          >
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Link to="/">
                <LogoImg src={logo1} alt="Logo" />
              </Link>
              <Box sx={{ display: { xs: "none", md: "flex" }, mr: 2 }}>
                <Link to="/" style={{ textDecoration: "none" }}>
                  <NavButton variant="text" size="small">
                    Home
                  </NavButton>
                </Link>
                <Link to="/aboutus" style={{ textDecoration: "none" }}>
                  <NavButton variant="text" size="small">
                    About Us
                  </NavButton>
                </Link>
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: 1,
                alignItems: "center",
              }}
            >
              <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={handleContactDialogOpen}
                sx={{
                  fontWeight: 500,
                  textTransform: "none",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                  },
                }}
              >
                Contact Us
              </Button>
            </Box>
            <Box sx={{ display: { xs: "flex", md: "none" }, gap: 1 }}>
              <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
                <MenuIcon sx={{ color: "#115097" }} />
              </IconButton>
              <Drawer
                anchor="top"
                open={open}
                onClose={toggleDrawer(false)}
                PaperProps={{
                  sx: {
                    top: "var(--template-frame-height, 0px)",
                    borderRadius: "0 0 8px 8px",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                  },
                }}
              >
                <Box sx={{ p: 2, backgroundColor: "#ffffff" }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Link to="/">
                      <LogoImg src={logo1} alt="Logo" />
                    </Link>
                    <IconButton onClick={toggleDrawer(false)}>
                      <CloseRoundedIcon sx={{ color: "#115097" }} />
                    </IconButton>
                  </Box>
                  <DrawerMenuItem>Home</DrawerMenuItem>
                  <Link to="/aboutus" style={{ textDecoration: "none" }}>
                    <DrawerMenuItem>About Us</DrawerMenuItem>
                  </Link>
                  <DrawerMenuItem>
                    <Button
                      color="primary"
                      variant="contained"
                      fullWidth
                      onClick={handleContactDialogOpen}
                      sx={{ fontWeight: 500 }}
                    >
                      Contact Us
                    </Button>
                  </DrawerMenuItem>
                </Box>
              </Drawer>
            </Box>
          </StyledToolbar>
        </Container>
      </AppBar>
      <ContactUsDialog
        open={contactDialogOpen}
        onClose={handleContactDialogClose}
      />
    </>
  );
}
